import React, { useState, useEffect } from "react";
import Select from "react-select";
import API from "../../../services/api";
import { BrandsTypes } from "../../../utils/constants";

const filteredBrandsTypes = BrandsTypes.filter((o) => !o.label.toLowerCase().includes("alpha"));

const selectStyles = {
    control: (base) => ({ ...base, fontSize: "13px", minHeight: "34px" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    indicatorSeparator: () => ({ display: "none" }),
};

/**
 * Shared filter bar for Campaign Reports.
 *
 * Props (visibility flags):
 *   showMonth       – show month input
 *   showBrandType   – show Brand Type dropdown
 *   showSalesPerson – show Sales Person dropdown
 *   showBrand       – show Brand dropdown (fetched per sales person)
 *
 * Props (values + setters):
 *   month / onMonthChange
 *   brandType / onBrandTypeChange
 *   salesPerson / onSalesPersonChange
 *   brand / onBrandChange
 *
 * Optional slot (rendered on the right):
 *   actions – React node(s) such as a Download CSV button
 */
const FilterBar = ({
    showMonth = false,
    showBrandType = false,
    showSalesPerson = false,
    showBrand = false,
    month,
    onMonthChange,
    brandType,
    onBrandTypeChange,
    salesPerson,
    onSalesPersonChange,
    brand,
    onBrandChange,
    actions,
}) => {
    const [salesPersonOptions, setSalesPersonOptions] = useState([]);
    const [brandOptions, setBrandOptions] = useState([]);

    // Fetch sales persons whenever brand-type filter changes.
    // When the active tab hides the brand-type filter, still fetch all persons (user_type: null).
    useEffect(() => {
        if (!showSalesPerson) {
            setSalesPersonOptions([]);
            return;
        }
        const fetchSalesPersons = async () => {
            const result = await API({
                url: "/user/sales_persons/getSalesByNewUserType",
                method: "POST",
                data: { user_type: showBrandType ? brandType?.value || null : null },
            });
            if (result?.status === "SUCCESS") setSalesPersonOptions(result.data);
        };
        fetchSalesPersons();
    }, [brandType, showSalesPerson, showBrandType]);

    // Reset sales person when brand type changes (only relevant when brand type filter is visible).
    useEffect(() => {
        if (!showBrandType) return;
        onSalesPersonChange?.(null);
    }, [brandType]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch brands whenever sales person changes (only when brand filter is visible).
    useEffect(() => {
        if (!showBrand) {
            setBrandOptions([]);
            return;
        }
        if (!salesPerson) {
            setBrandOptions([]);
            onBrandChange?.(null);
            return;
        }
        const fetchBrands = async () => {
            const result = await API({
                url: "/user/campaign_creation/getBrands",
                method: "POST",
                data: { sales_person_id: salesPerson.value },
            });
            if (result?.code === 200) {
                setBrandOptions(
                    result.data.map((b) => ({ value: b.brand_id, label: b.brand_name }))
                );
            }
        };
        onBrandChange?.(null);
        fetchBrands();
    }, [salesPerson, showBrand]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="flex flex-wrap gap-3 items-end justify-between px-4 py-3 bg-white border-b border-gray-200">
            <div className="flex flex-wrap gap-3 items-end">
                {showMonth && (
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Month
                        </label>
                        <input
                            type="month"
                            value={month}
                            onChange={(e) => onMonthChange(e.target.value)}
                            className="border border-gray-300 rounded px-2.5 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                )}

                {showBrandType && (
                    <div className="flex flex-col gap-1 min-w-[180px]">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Brand Type
                        </label>
                        <Select
                            options={filteredBrandsTypes}
                            value={brandType}
                            onChange={onBrandTypeChange}
                            isClearable
                            placeholder="All Brand Types"
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                        />
                    </div>
                )}

                {showSalesPerson && (
                    <div className="flex flex-col gap-1 min-w-[200px]">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Sales Person
                        </label>
                        <Select
                            options={salesPersonOptions}
                            value={salesPerson}
                            onChange={onSalesPersonChange}
                            isClearable
                            placeholder="All Sales Persons"
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                        />
                    </div>
                )}

                {showBrand && (
                    <div className="flex flex-col gap-1 min-w-[200px]">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Brand
                        </label>
                        <Select
                            options={brandOptions}
                            value={brand}
                            onChange={onBrandChange}
                            isClearable
                            isDisabled={!salesPerson}
                            placeholder={salesPerson ? "All Brands" : "Select a sales person first"}
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                        />
                    </div>
                )}
            </div>

            {actions && <div className="flex items-end">{actions}</div>}
        </div>
    );
};

export default FilterBar;
